const CONTENTFUL_IMAGE_HOST = 'images.ctfassets.net'
const DEFAULT_PNG_QUALITY = 80

interface ContentfulImageOptions {
  w?: number
  h?: number
  q?: number
  fm?: 'webp' | 'avif' | 'jpg' | 'png'
  fit?: 'pad' | 'fill' | 'scale' | 'crop' | 'thumb'
}

/**
 * Rewrites a Contentful image URL to the same-origin /cf-assets proxy so bytes
 * are served from the edge cache instead of Contentful's CDN, with Image API
 * transforms applied upstream. Non-Contentful URLs pass through unchanged.
 */
export const contentfulImage = (
  url: string | undefined,
  { fm = 'webp', ...options }: ContentfulImageOptions = {},
): string | undefined => {
  if (!url) return undefined
  const absolute = url.startsWith('//') ? `https:${url}` : url

  let parsed: URL
  try {
    parsed = new URL(absolute)
  } catch {
    return absolute
  }
  if (parsed.hostname !== CONTENTFUL_IMAGE_HOST) return absolute

  const pathname = parsed.pathname
  const lower = pathname.toLowerCase()
  const params = new URLSearchParams()

  // The Images API can't transform SVGs, and format conversion drops GIF animation
  if (!lower.endsWith('.svg')) {
    const transforms: Record<string, string | number | undefined> = {
      // Contentful's default quality tracks the source, so a PNG comes back as
      // near-lossless WebP. JPEG sources are already compressed sensibly and
      // only grow if we override them, so they keep the default.
      q: lower.endsWith('.png') ? DEFAULT_PNG_QUALITY : undefined,
      ...options,
      fm: lower.endsWith('.gif') ? undefined : fm,
    }
    for (const [key, value] of Object.entries(transforms)) {
      if (value !== undefined) params.set(key, String(value))
    }
  }

  const query = params.toString()
  return query ? `/cf-assets${pathname}?${query}` : `/cf-assets${pathname}`
}
